import { Linking } from 'react-native';

import { reportError } from '@/lib/errors';

/** Matches a URI scheme prefix like `https:`, `mailto:`, `tel:`, `inverio:`. */
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Normalize a user/AI-supplied link before handing it to `Linking.openURL`.
 *
 * - Leaves anything with an explicit scheme intact (`https:`, `mailto:`, `tel:`,
 *   `inverio:` deep links, …).
 * - Leaves root-relative web routes intact (`/terms`) — never corrupts them to
 *   `https:///terms`.
 * - Prepends `https://` to a bare host (`www.rentis.io`). Without this, iOS
 *   resolves a schemeless string as a local `file://` path and throws
 *   `Unable to open URL: file://…/www.rentis.io` (the Sentry crash this fixes).
 *
 * Returns `null` for empty / whitespace-only / nullish input.
 */
export function normalizeExternalUrl(raw: string | null | undefined): string | null {
  const url = raw?.trim();
  if (!url) return null;
  if (SCHEME_RE.test(url)) return url; // explicit scheme — leave as-is
  if (url.startsWith('/')) return url; // root-relative web route — leave as-is
  return `https://${url}`; // bare host — force https so iOS doesn't treat it as a file path
}

/**
 * Open an external link safely. Normalizes a bare host to `https://` and routes
 * any failure through the centralized error reporter instead of throwing
 * uncaught. Use this for every outbound link sourced from user input, AI
 * extraction, or remote data — do not call `Linking.openURL` directly for those.
 */
export async function openExternalUrl(
  raw: string | null | undefined,
  context = 'openExternalUrl',
): Promise<void> {
  const url = normalizeExternalUrl(raw);
  if (!url) return;
  try {
    await Linking.openURL(url);
  } catch (err) {
    reportError(err, { context, level: 'warning' });
  }
}
